import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
import { palette } from '../../tokens/palette';

/* ─────────────────────────── Types ────────────────────────────────────── */

export type InputGroupVariant = 'inline' | 'attached';

export interface InputGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * `'inline'` (default) — addons float **inside** the input border via absolute positioning.
   * Matches production YEAR/DAYS/%/password-toggle pattern.
   *
   * `'attached'` — addons sit inside the input with a subtle pill/tag background.
   * Unified visual language — no harsh divider lines.
   */
  variant?: InputGroupVariant;
  error?: boolean;
  disabled?: boolean;
  semanticId?: string;
  children: React.ReactNode;
}

export interface InputGroupAddonProps extends React.HTMLAttributes<HTMLDivElement> {
  position?: 'start' | 'end';
  /** Clickable addon (e.g., password toggle, copy button). */
  interactive?: boolean;
  semanticId?: string;
  children: React.ReactNode;
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Constants                                                              */
/* ═══════════════════════════════════════════════════════════════════════ */

const ADDON_GAP = 8;
const INLINE_ICON_INSET = 14;
const ATTACHED_PILL_INSET = 6;

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Root                                                                   */
/* ═══════════════════════════════════════════════════════════════════════ */

const TRANSIENT_ROOT = ['$error', '$disabled'] as const;
const shouldForwardRoot = (prop: string | number): boolean =>
  !TRANSIENT_ROOT.includes(prop as (typeof TRANSIENT_ROOT)[number]);

const StyledRoot = styled.div.withConfig({ shouldForwardProp: shouldForwardRoot })<{
  $error: boolean;
  $disabled: boolean;
}>`
  position: relative;
  width: 100%;

  ${({ $disabled }) =>
    $disabled &&
    css`
      /* Fill-based disabled (no opacity) so addon fields match plain TextInput. */
      pointer-events: none;
      cursor: not-allowed;

      input,
      select,
      textarea {
        background-color: var(--ui-background-muted) !important;
        border-color: var(--ui-border) !important;
        color: var(--ui-text);
        -webkit-text-fill-color: var(--ui-text);
      }
    `}

  ${({ $error }) =>
    $error &&
    css`
      input,
      select,
      textarea {
        border-color: var(--ui-status-danger) !important;
      }
    `}

  /*
   * Input padding is driven by CSS custom properties set at runtime
   * via ResizeObserver. Fallback to default input padding (12px).
   */
  input,
  select,
  textarea {
    padding-left: var(--ig-pad-left, ${rem(12)}) !important;
    padding-right: var(--ig-pad-right, ${rem(12)}) !important;
  }
`;

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Inline Addon — absolute-positioned inside the input                   */
/* ═══════════════════════════════════════════════════════════════════════ */

const TRANSIENT_ADDON = ['$position', '$interactive'] as const;
const shouldForwardAddon = (prop: string | number): boolean =>
  !TRANSIENT_ADDON.includes(prop as (typeof TRANSIENT_ADDON)[number]);

const StyledInlineAddon = styled.div.withConfig({ shouldForwardProp: shouldForwardAddon })<{
  $position: 'start' | 'end';
  $interactive: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(14)};
  font-weight: 400;
  color: var(--ui-text-muted, ${palette.Gray});
  white-space: nowrap;
  user-select: none;

  ${({ $position }) =>
    $position === 'start'
      ? css`left: ${rem(INLINE_ICON_INSET)};`
      : css`right: ${rem(INLINE_ICON_INSET)};`}

  .material-symbols-outlined {
    font-size: ${rem(20)};
    color: inherit;
  }

  ${({ $interactive }) =>
    $interactive &&
    css`
      cursor: pointer;
      transition: color 0.15s ease;

      &:hover {
        color: var(--ui-text, ${palette.Black});
      }
    `}
`;

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Attached Addon — inside the input with a subtle pill background       */
/* ═══════════════════════════════════════════════════════════════════════ */

const StyledAttachedAddon = styled.div.withConfig({ shouldForwardProp: shouldForwardAddon })<{
  $position: 'start' | 'end';
  $interactive: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  padding: ${rem(6)} ${rem(10)};
  background: var(--ui-background-muted, ${palette.OffWhite});
  border: ${rem(1)} solid var(--ui-background-muted, ${palette.OffWhite});
  border-radius: var(--ui-radius-sm, ${rem(4)});
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(12)};
  font-weight: 500;
  line-height: ${rem(14)};
  color: var(--ui-text-muted, ${palette.Gray});
  white-space: nowrap;
  user-select: none;

  ${({ $position }) =>
    $position === 'start'
      ? css`left: ${rem(ATTACHED_PILL_INSET)};`
      : css`right: ${rem(ATTACHED_PILL_INSET)};`}

  .material-symbols-outlined {
    font-size: ${rem(18)};
    color: inherit;
  }

  ${({ $interactive }) =>
    $interactive &&
    css`
      cursor: pointer;
      transition: background-color 0.15s ease, color 0.15s ease;

      &:hover {
        background-color: var(--ui-surface-hover, ${palette.CulturedWhite});
        color: var(--ui-text, ${palette.Black});
      }
      &:active {
        background-color: var(--ui-surface-pressed, var(--ui-border, #ddd));
      }
    `}
`;

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Tag — production YEAR/DAYS/% badge                                    */
/* ═══════════════════════════════════════════════════════════════════════ */

const StyledInputTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--ui-background-muted, ${palette.OffWhite});
  border: ${rem(1)} solid var(--ui-background-muted, ${palette.OffWhite});
  border-radius: var(--ui-radius-sm, ${rem(4)});
  padding: ${rem(9)};
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(12)};
  font-weight: 500;
  line-height: ${rem(14)};
  color: var(--ui-text-muted, ${palette.Gray});
  white-space: nowrap;
`;

/* ─────────────────────────── data-role constants ──────────────────────── */

const ROLE_ADDON_START = 'addon-start';
const ROLE_ADDON_END = 'addon-end';

/* ─────────────────────────── Root component ──────────────────────────── */

const InputGroupRoot = React.forwardRef<HTMLDivElement, InputGroupProps>(
  function InputGroupRoot(
    { variant = 'inline', error = false, disabled = false, semanticId, children, ...rest },
    ref,
  ) {
    const semanticProps = getSemanticAttributes(semanticId);
    const rootRef = React.useRef<HTMLDivElement | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        rootRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref],
    );

    React.useEffect(() => {
      const root = rootRef.current;
      if (!root) return undefined;

      const measure = (): void => {
        const startEl = root.querySelector<HTMLElement>(`[data-role="${ROLE_ADDON_START}"]`);
        const endEl = root.querySelector<HTMLElement>(`[data-role="${ROLE_ADDON_END}"]`);

        if (startEl) {
          const w = startEl.offsetWidth;
          const inset = startEl.dataset.attached != null ? ATTACHED_PILL_INSET : INLINE_ICON_INSET;
          root.style.setProperty('--ig-pad-left', `${inset + w + ADDON_GAP}px`);
        } else {
          root.style.removeProperty('--ig-pad-left');
        }

        if (endEl) {
          const w = endEl.offsetWidth;
          const inset = endEl.dataset.attached != null ? ATTACHED_PILL_INSET : INLINE_ICON_INSET;
          root.style.setProperty('--ig-pad-right', `${inset + w + ADDON_GAP}px`);
        } else {
          root.style.removeProperty('--ig-pad-right');
        }
      };

      measure();

      const observer = new ResizeObserver(measure);

      const startEl = root.querySelector<HTMLElement>(`[data-role="${ROLE_ADDON_START}"]`);
      const endEl = root.querySelector<HTMLElement>(`[data-role="${ROLE_ADDON_END}"]`);
      if (startEl) observer.observe(startEl);
      if (endEl) observer.observe(endEl);

      return () => observer.disconnect();
    });

    return (
      <StyledRoot
        ref={setRefs}
        $error={error}
        $disabled={disabled}
        data-ui-input-group=""
        data-error={error || undefined}
        data-disabled={disabled || undefined}
        data-variant={variant}
        {...semanticProps}
        {...rest}
      >
        {children}
      </StyledRoot>
    );
  },
);

InputGroupRoot.displayName = 'InputGroup';

/* ─────────────────────────── Addon ────────────────────────────────────── */

const InputGroupAddon = React.forwardRef<HTMLDivElement, InputGroupAddonProps>(
  function InputGroupAddon(
    { position = 'end', interactive = false, semanticId, children, ...rest },
    ref,
  ) {
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <StyledInlineAddon
        ref={ref}
        $position={position}
        $interactive={interactive}
        data-role={position === 'start' ? ROLE_ADDON_START : ROLE_ADDON_END}
        {...semanticProps}
        {...rest}
      >
        {children}
      </StyledInlineAddon>
    );
  },
);

InputGroupAddon.displayName = 'InputGroup.Addon';

/* ─────────────────────────── AttachedAddon ────────────────────────────── */

const InputGroupAttachedAddon = React.forwardRef<HTMLDivElement, InputGroupAddonProps>(
  function InputGroupAttachedAddon(
    { position = 'start', interactive = false, semanticId, children, ...rest },
    ref,
  ) {
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <StyledAttachedAddon
        ref={ref}
        $position={position}
        $interactive={interactive}
        data-role={position === 'start' ? ROLE_ADDON_START : ROLE_ADDON_END}
        data-attached=""
        {...semanticProps}
        {...rest}
      >
        {children}
      </StyledAttachedAddon>
    );
  },
);

InputGroupAttachedAddon.displayName = 'InputGroup.AttachedAddon';

/* ─────────────────────────── Tag (inline badge) ──────────────────────── */

export interface InputGroupTagProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
}

const InputGroupTag = React.forwardRef<HTMLSpanElement, InputGroupTagProps>(
  function InputGroupTag({ children, ...rest }, ref) {
    return (
      <StyledInputTag ref={ref} {...rest}>
        {children}
      </StyledInputTag>
    );
  },
);

InputGroupTag.displayName = 'InputGroup.Tag';

/* ─────────────────────────── Compound export ──────────────────────────── */

export const InputGroup = Object.assign(InputGroupRoot, {
  Addon: InputGroupAddon,
  AttachedAddon: InputGroupAttachedAddon,
  Tag: InputGroupTag,
});
